<script>
  import { createEventDispatcher } from 'svelte';
  import { onMount } from 'svelte';
  import { writable, get, derived } from 'svelte/store';
  import { theme } from '$stores/themeStore';
  import { selectedStudy } from '$stores/selectionStore';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import MultiSelectDropdown from '$lib/MultiSelectDropdown.svelte';

  // Using proxy endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  export let showForm = false;
  export let editActivity = null;
  export let scheduleGUID = null;
  export let scheduleStatus = null;

  let activityTypes = [];
  let devices = [];
  let forms = [];
  let activityData = writable({
    name: '',
    code: '',
    description: null,
    order: 0,
    startTime: null,
    gap: 0,
    windowMin: 0,
    windowMax: 0,
    readingDuration: 0,
    readingDurationDisplayUnitGUID: null,
    readingFrequency: 0,
    readingFrequencyDisplayUnitGUID: null,
    readingCount: 0,
    complianceThreshold: 0,
    activityTypeGUID: null,
    deviceGUID: null,
    deviceGUIDs: [],
    formGUID: null,
    formGUIDs: [],
    active: true,
    onDemand: false,
    allowDateEntryIndicator: false,
    enableAlerts: false,
    complianceWindowMin: 0,
    complianceWindowMax: 0,
    cgmBlinded: false,
    smbgBlinded: false,
  });

  // Keep a direct reference to the current activity type
  let directSelectedActivityType = null;

  // Fix cyclical dependency by using a derived store
  const selectedActivityTypeId = derived(activityData, ($data) => $data.activityTypeGUID);
  $: selectedActivityType =
    activityTypes.find((t) => t.id === $selectedActivityTypeId) || directSelectedActivityType;
  $: isFormActivity = selectedActivityType?.code === 'FORM';
  $: isMeasurementActivity = selectedActivityType?.code === 'MEAS';

  // Determine if save button should be disabled - for Draft status check
  $: isAdmin = hasAccessUiAction(
    'studySchedules.activityForm.admin',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  $: saveButtonDisabled = $userReadOnly || scheduleStatus !== 'Draft';

  // Determine if form fields should be disabled - only for non-Draft schedules
  $: formFieldsDisabled = scheduleStatus !== 'Draft' && !isAdmin;

  const dispatch = createEventDispatcher();

  onMount(async () => {
    // First fetch the lookups
    await Promise.all([fetchActivityTypes(), fetchDevices(), fetchForms()]);

    // Initialize form data after lookups are loaded
    initializeFormData();
  });

  // Normalize helpers to align IDs with <option> string values
  function normalizeId(value) {
    if (value === undefined || value === null) return '';
    // Support raw ids, ObjectId-like objects, and objects with _id
    if (typeof value === 'object') {
      // Handle Mongo Extended JSON shape
      if (value.$oid && typeof value.$oid === 'string') return value.$oid;
      if (value._id) return normalizeId(value._id);
      if (typeof value.toString === 'function') {
        const s = value.toString();
        // If toString returns a 24-char hex, use it; else fallback to stringification
        if (typeof s === 'string' && /^[a-fA-F0-9]{24}$/.test(s)) return s;
        return String(s || '');
      }
      return '';
    }
    return String(value);
  }

  function normalizeIdsArray(arr) {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((v) => normalizeId(v))
      .filter((v) => v !== '');
  }

  // Build items for dropdowns
  $: formItems = (forms || []).map((f) => ({ value: normalizeId(f._id), label: f.name }));
  $: deviceItems = (devices || []).map((d) => ({ value: normalizeId(d._id), label: d.name }));

  // Watch for changes to editActivity or showForm
  let previousActivityId = null;
  $: {
    // Initialize when component is shown or when editActivity changes
    if (showForm && activityTypes.length > 0) {
      const currentActivityId = editActivity?._id || 'new';
      if (currentActivityId !== previousActivityId) {
        previousActivityId = currentActivityId;
        initializeFormData();
      }
    }
  }

  // Function to initialize form data
  function initializeFormData() {
    if (editActivity) {
      // Convert seconds to days
      const secondsToDays = (seconds) => (seconds ? Math.round(seconds / (24 * 60 * 60)) : 0);
      // Convert seconds to minutes for reading frequency
      const secondsToMinutes = (seconds) => (seconds ? Math.round(seconds / 60) : 0);

      // Find matching activity type by code - add more detailed logging
      const matchingActivityType = activityTypes.find(
        (t) => t.code === editActivity.activityTypeCode
      );

      // If no direct match found by code, try matching by ID
      let typeToUse = matchingActivityType;
      if (!typeToUse && editActivity.activityTypeGUID) {
        console.log(
          'No match found by code, trying to match by ID:',
          editActivity.activityTypeGUID
        );
        typeToUse = activityTypes.find((t) => t.id === editActivity.activityTypeGUID);
      }

      // Set the direct reference to ensure UI updates immediately
      directSelectedActivityType = typeToUse;

      // Find matching device by name
      const matchingDevice = devices.find((d) => d.name === editActivity.deviceName);

      // Find matching form - try by ID first, then by name if needed
      let matchingForm = null;
      if (editActivity.formGUID) {
        matchingForm = forms.find((f) => f._id === editActivity.formGUID);

        // If not found by direct GUID, try by string comparison
        if (!matchingForm) {
          matchingForm = forms.find((f) => f._id.toString() === editActivity.formGUID.toString());
        }
      } else if (editActivity.formName) {
        matchingForm = forms.find((f) => f.name === editActivity.formName);
      }

      const initialData = {
        ...editActivity,
        // Convert gap and readingDuration from seconds to days for display
        gap: secondsToDays(editActivity.gap),
        readingDuration: secondsToDays(editActivity.readingDuration || 0),
        // Convert reading frequency from seconds to minutes
        readingFrequency: secondsToMinutes(editActivity.readingFrequency || 0),
        // Ensure all fields are properly set
        windowMin: editActivity.windowMin ?? 0,
        windowMax: editActivity.windowMax ?? 0,
        readingCount: editActivity.readingCount ?? 0,
        complianceThreshold: editActivity.complianceThreshold ?? 0,
        complianceWindowMin: editActivity.complianceWindowMin ?? 0,
        complianceWindowMax: editActivity.complianceWindowMax ?? 0,
        // Map activity type, device, and form GUIDs from the matching records
        activityTypeGUID: typeToUse?.id || editActivity.activityTypeGUID || '',
        deviceGUID: normalizeId(editActivity.deviceGUID) || matchingDevice?._id || '',
        deviceGUIDs: normalizeIdsArray(editActivity.deviceGUIDs),
        formGUID: normalizeId(matchingForm?._id || editActivity.formGUID),
        formGUIDs: normalizeIdsArray(editActivity.formGUIDs),
        // Ensure boolean fields are set
        active: editActivity.active ?? true,
        onDemand: editActivity.onDemand ?? false,
        allowDateEntryIndicator: editActivity.allowDateEntryIndicator ?? false,
        enableAlerts: editActivity.enableAlerts ?? false,
        cgmBlinded: editActivity.cgmBlinded ?? false,
        smbgBlinded: editActivity.smbgBlinded ?? false,
        // Ensure description is properly set
        description: editActivity.description || null,
      };

      activityData.set(initialData);
    } else {
      // Reset the direct reference for new activities
      directSelectedActivityType = null;

      // Initialize with default values for new activity
      activityData.set({
        name: '',
        code: '',
        description: null,
        order: 0,
        startTime: null,
        gap: 0,
        windowMin: 0,
        windowMax: 0,
        readingDuration: 0,
        readingDurationDisplayUnitGUID: null,
        readingFrequency: 0,
        readingFrequencyDisplayUnitGUID: null,
        readingCount: 0,
        complianceThreshold: 0,
        activityTypeGUID: '',
        deviceGUID: '',
        deviceGUIDs: [],
        formGUID: '',
        formGUIDs: [],
        active: true,
        onDemand: false,
        allowDateEntryIndicator: false,
        enableAlerts: false,
        complianceWindowMin: 0,
        complianceWindowMax: 0,
        cgmBlinded: false,
        smbgBlinded: false,
        scheduleGUID,
      });
    }
  }

  // Watch for changes to editActivity and activityTypes
  $: {
    if (editActivity && activityTypes.length > 0) {
      // Debug: Check if we can find a matching activity type
      const matchingType = activityTypes.find((t) => t.id === $activityData.activityTypeGUID);

      if (!matchingType) {
        console.log('No match found. Comparing all values:');
        activityTypes.forEach((t) => {
          console.log(
            `Comparing:\n  Type.id: ${t.id} (${typeof t.id})\n  ActivityTypeGUID: ${$activityData.activityTypeGUID} (${typeof $activityData.activityTypeGUID})`
          );
        });
      }
    }
  }

  // Watch for activity type changes to reset device/form as necessary
  $: {
    if ($activityData.activityTypeGUID) {
      const currentType = activityTypes.find((t) => t.id === $activityData.activityTypeGUID);

      // Update the direct reference to keep it in sync
      directSelectedActivityType = currentType;

      if (currentType) {
        if (currentType.code === 'FORM') {
          // Reset device fields and group form array when switching to single Form
          $activityData.deviceGUID = '';
          $activityData.deviceGUIDs = [];
          $activityData.formGUIDs = [];
        } else if (currentType.code === 'FORMGROUP') {
          // Reset device fields and single form when switching to Form Group
          $activityData.deviceGUID = '';
          $activityData.deviceGUIDs = [];
          $activityData.formGUID = '';
        } else if (currentType.code === 'MEAS') {
          // Reset form fields and group device array when switching to single Measurement
          $activityData.formGUID = '';
          $activityData.formGUIDs = [];
          $activityData.deviceGUIDs = [];
        } else if (currentType.code === 'MEASGROUP') {
          // Reset form fields and single device when switching to Measurement Group
          $activityData.formGUID = '';
          $activityData.formGUIDs = [];
          $activityData.deviceGUID = '';
        } else {
          // Reset all for other activity types
          $activityData.deviceGUID = '';
          $activityData.deviceGUIDs = [];
          $activityData.formGUID = '';
          $activityData.formGUIDs = [];
        }
      }
    }
  }

  async function fetchActivityTypes() {
    try {
      const res = await fetch(`${API_BASE_URL}/lookups/ACTYP`);

      if (!res.ok) {
        throw new Error('Failed to fetch activity types');
      }

      const data = await res.json();

      // Map the lookup data using the id field
      const allTypes = (data.data || []).map((type) => ({
        id: type.id, // Keep original format
        label: type.label,
        code: type.code,
      }));

      // Filter to only include Form, Measurement, and Milestone
      activityTypes = allTypes.filter(
        (type) => type.code === 'FORM' || type.code === 'FORMGROUP' || type.code === 'MEAS' || type.code === 'MEASGROUP' || type.code === 'MILE'
      );
    } catch (err) {
      console.error('Error fetching activity types:', err);
      activityTypes = [];
    }
  }

  async function fetchDevices() {
    try {
      const res = await fetch(`${API_BASE_URL}/devices?page=1&pageSize=999`);

      if (!res.ok) {
        throw new Error('Failed to fetch devices');
      }

      const data = await res.json();
      devices = data.data || [];
    } catch (err) {
      console.error('Error fetching devices:', err);
      devices = [];
    }
  }

  async function fetchForms() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/forms?&pageSize=999&studyGUID=${$selectedStudy._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch forms');
      }

      const data = await res.json();
      forms = data.data || [];
    } catch (err) {
      console.error('Error fetching forms:', err);
      forms = [];
    }
  }

  function handleSubmit() {
    // Convert days to seconds
    const daysToSeconds = (days) => (days ? days * 24 * 60 * 60 : 0);
    // Convert minutes to seconds for reading frequency
    const minutesToSeconds = (minutes) => (minutes ? minutes * 60 : 0);

    // Use direct reference for more reliable type checking
    const isForm = directSelectedActivityType?.code === 'FORM';
    const isFormGroup = directSelectedActivityType?.code === 'FORMGROUP';
    const isMeasurement = directSelectedActivityType?.code === 'MEAS';
    const isMeasurementGroup = directSelectedActivityType?.code === 'MEASGROUP';

    const submitData = {
      ...$activityData,
      scheduleGUID,
      // Convert gap and readingDuration from days to seconds
      gap: daysToSeconds($activityData.gap),
      readingDuration: daysToSeconds($activityData.readingDuration),
      // Convert reading frequency from minutes to seconds
      readingFrequency: minutesToSeconds($activityData.readingFrequency),
      // Ensure numeric fields are properly typed
      order: Number($activityData.order || 0),
      windowMin: Number($activityData.windowMin || 0),
      windowMax: Number($activityData.windowMax || 0),
      readingCount: Number($activityData.readingCount || 0),
      complianceThreshold: Number($activityData.complianceThreshold || 0),
      complianceWindowMin: Number($activityData.complianceWindowMin || 0),
      complianceWindowMax: Number($activityData.complianceWindowMax || 0),
      // Ensure GUIDs are properly handled based on activity type
      activityTypeGUID: $activityData.activityTypeGUID || null,
      deviceGUID: isMeasurement ? $activityData.deviceGUID || null : null,
      deviceGUIDs: isMeasurementGroup
        ? ($activityData.deviceGUIDs && $activityData.deviceGUIDs.length
            ? $activityData.deviceGUIDs
            : null)
        : null,
      formGUID: isForm ? $activityData.formGUID || null : null,
      formGUIDs: isFormGroup
        ? ($activityData.formGUIDs && $activityData.formGUIDs.length
            ? $activityData.formGUIDs
            : null)
        : null,
      // Ensure boolean fields are properly typed
      active: Boolean($activityData.active),
      onDemand: Boolean($activityData.onDemand),
      allowDateEntryIndicator: Boolean($activityData.allowDateEntryIndicator),
      enableAlerts: Boolean($activityData.enableAlerts),
      cgmBlinded: Boolean($activityData.cgmBlinded),
      smbgBlinded: Boolean($activityData.smbgBlinded),
    };

    // Remove fields that shouldn't be stored
    delete submitData._id; // Let MongoDB generate this for new activities
    delete submitData.activityTypeName;
    delete submitData.activityTypeCode;
    delete submitData.deviceName;
    delete submitData.formName;
    delete submitData.id; // Remove if exists

    // If this is an update operation and we have an _id from editActivity, add it back
    if (editActivity?._id) {
      submitData._id = editActivity._id;
    }

    // Clean up any undefined or null values that shouldn't be sent
    Object.keys(submitData).forEach((key) => {
      if (submitData[key] === undefined || submitData[key] === null) {
        delete submitData[key];
      }
    });

    dispatch('submit', submitData);
  }

  function handleClose() {
    dispatch('close');
  }
</script>

{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={handleClose}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-2xl relative z-60"
      on:click|stopPropagation
    >
      <header class="flex justify-between items-start mb-4">
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
          {editActivity ? 'Edit Activity' : 'New Activity'}
        </h2>
        <button
          on:click={handleClose}
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </header>
      <form on:submit|preventDefault={handleSubmit} class="space-y-4">
        <!-- Order, Activity Type, Device/Form -->
        <div class="flex gap-3">
          <div class="w-1/5">
            <label for="order" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Order</label
            >
            <input
              type="number"
              id="order"
              bind:value={$activityData.order}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
              required
            />
          </div>
          <div class="w-2/5">
            <label for="activityType" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Activity Type</label
            >
            <select
              id="activityType"
              bind:value={$activityData.activityTypeGUID}
              disabled={formFieldsDisabled}
              on:change={(e) => {
                // Force update the direct reference
                directSelectedActivityType = activityTypes.find((t) => t.id === e.target.value);
              }}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            >
              <option value="">Select Type</option>
              {#each activityTypes as type}
                <option value={type.id}>{type.label}</option>
              {/each}
            </select>
          </div>

          <!-- Device Selector (shown for MEAS activity type) -->
          <div
            class="w-2/5"
            style="display: {directSelectedActivityType?.code === 'MEAS' ? 'block' : 'none'}"
          >
            <label for="device" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Device</label
            >
            <select
              id="device"
              bind:value={$activityData.deviceGUID}
              disabled={formFieldsDisabled}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            >
              <option value="">Select Device</option>
              {#each devices as device}
                <option value={device._id}>{device.name}</option>
              {/each}
            </select>
          </div>

          <!-- Device Multi-Select (shown for MEASGROUP activity type) -->
          <div
            class="w-2/5"
            style="display: {directSelectedActivityType?.code === 'MEASGROUP' ? 'block' : 'none'}"
          >
            <span class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Devices</span>
            <MultiSelectDropdown
              items={deviceItems}
              bind:selectedValues={$activityData.deviceGUIDs}
              disabled={formFieldsDisabled}
              placeholder="Select devices"
            />
          </div>

          <!-- Form Selector (shown for FORM activity type) -->
          <div
            class="w-1/2"
            style="display: {directSelectedActivityType?.code === 'FORM' ? 'block' : 'none'}"
          >
            <label for="form" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Form</label
            >
            <select
              id="form"
              bind:value={$activityData.formGUID}
              disabled={formFieldsDisabled}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            >
              <option value="">Select Form</option>
              {#each forms as form}
                <option value={form._id}>{form.name}</option>
              {/each}
            </select>
          </div>

          <!-- Form Multi-Select (shown for FORMGROUP activity type) -->
          <div
            class="w-1/2"
            style="display: {directSelectedActivityType?.code === 'FORMGROUP' ? 'block' : 'none'}"
          >
            <span class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Forms</span>
            <MultiSelectDropdown
              items={formItems}
              bind:selectedValues={$activityData.formGUIDs}
              disabled={formFieldsDisabled}
              placeholder="Select forms"
            />
          </div>

          <!-- Empty placeholder for other activity types -->
          <div
            class="w-1/2"
            style="display: {!directSelectedActivityType ||
            (directSelectedActivityType.code !== 'MEAS' &&
              directSelectedActivityType.code !== 'MEASGROUP' &&
              directSelectedActivityType.code !== 'FORM' &&
              directSelectedActivityType.code !== 'FORMGROUP')
              ? 'block'
              : 'none'}"
          ></div>
        </div>

        <!-- Name, Code, Active -->
        <div class="flex gap-3">
          <div class="w-3/5">
            <label for="name" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Name</label
            >
            <input
              type="text"
              id="name"
              bind:value={$activityData.name}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
              required
            />
          </div>
          <div class="w-1/5">
            <label for="code" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Code</label
            >
            <input
              type="text"
              id="code"
              bind:value={$activityData.code}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
          <div class="w-1/5">
            <label for="active" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Active</label
            >
            <select
              id="active"
              bind:value={$activityData.active}
              disabled={formFieldsDisabled}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            >
              <option value={true}>Yes</option>
              <option value={false}>No</option>
            </select>
          </div>
        </div>

        <!-- Duration and Windows -->
        <div
          class="flex gap-3"
          style="display: {directSelectedActivityType?.code === 'MILE' ? 'flex' : 'none'}"
        >
          <div class="w-1/3">
            <label for="gap" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Duration (days)</label
            >
            <input
              type="number"
              id="gap"
              bind:value={$activityData.gap}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
          <div class="w-1/3">
            <label for="windowMin" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Window Min</label
            >
            <input
              type="number"
              id="windowMin"
              bind:value={$activityData.windowMin}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
          <div class="w-1/3">
            <label for="windowMax" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Window Max</label
            >
            <input
              type="number"
              id="windowMax"
              bind:value={$activityData.windowMax}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
        </div>

        <!-- Reading Settings -->
        <!-- <div
          class="flex gap-3"
          style="display: {directSelectedActivityType?.code === 'MEAS' || directSelectedActivityType?.code === 'MEASGROUP' ? 'flex' : 'none'}"
        >
          <div class="w-1/3">
            <label for="readingDuration" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Reading Duration (days)</label
            >
            <input
              type="number"
              id="readingDuration"
              bind:value={$activityData.readingDuration}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
          <div class="w-1/3">
            <label
              for="readingFrequency"
              class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Reading Frequency (minutes)</label
            >
            <input
              type="number"
              id="readingFrequency"
              bind:value={$activityData.readingFrequency}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
          <div class="w-1/3">
            <label for="readingCount" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Reading Count</label
            >
            <input
              type="number"
              id="readingCount"
              bind:value={$activityData.readingCount}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
        </div> -->

        <!-- Compliance Settings -->
        <div
          class="flex gap-3"
          style="display: {directSelectedActivityType?.code === 'MEAS' || directSelectedActivityType?.code === 'MEASGROUP' ? 'flex' : 'none'}"
        >
          <div class="w-1/3">
            <label
              for="complianceThreshold"
              class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Compliance Threshold (%)</label
            >
            <input
              type="number"
              id="complianceThreshold"
              bind:value={$activityData.complianceThreshold}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
          <div class="w-1/3">
            <label
              for="complianceWindowMin"
              class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Compliance Min</label
            >
            <input
              type="number"
              id="complianceWindowMin"
              bind:value={$activityData.complianceWindowMin}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
          <div class="w-1/3">
            <label
              for="complianceWindowMax"
              class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Compliance Max</label
            >
            <input
              type="number"
              id="complianceWindowMax"
              bind:value={$activityData.complianceWindowMax}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
          </div>
        </div>

        <!-- Checkboxes -->
        <div class="grid grid-cols-2 gap-2">
          <div
            class="flex items-center"
            style="display: {directSelectedActivityType?.code !== 'MILE' ? 'flex' : 'none'}"
          >
            <input
              type="checkbox"
              id="onDemand"
              bind:checked={$activityData.onDemand}
              disabled={formFieldsDisabled}
              class="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
            <label for="onDemand" class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >On Demand</label
            >
          </div>

          <div
            class="flex items-center"
            style="display: {directSelectedActivityType?.code === 'MILE' ? 'flex' : 'none'}"
          >
            <input
              type="checkbox"
              id="allowDateEntryIndicator"
              bind:checked={$activityData.allowDateEntryIndicator}
              disabled={formFieldsDisabled}
              class="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
            <label
              for="allowDateEntryIndicator"
              class="ml-2 block text-sm text-gray-700 dark:text-gray-300">Allow Date Entry</label
            >
          </div>

          <div
            class="flex items-center"
            style="display: {directSelectedActivityType?.code === 'MILE' ? 'flex' : 'none'}"
          >
            <input
              type="checkbox"
              id="enableAlerts"
              bind:checked={$activityData.enableAlerts}
              disabled={formFieldsDisabled}
              class="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
            <label for="enableAlerts" class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >Enable Alerts</label
            >
          </div>

          <div
            class="flex items-center"
            style="display: {directSelectedActivityType?.code === 'MILE' ? 'flex' : 'none'}"
          >
            <input
              type="checkbox"
              id="cgmBlinded"
              bind:checked={$activityData.cgmBlinded}
              disabled={formFieldsDisabled}
              class="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
            <label for="cgmBlinded" class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >CGM Blinded</label
            >
          </div>

          <div
            class="flex items-center"
            style="display: {directSelectedActivityType?.code === 'MILE' ? 'flex' : 'none'}"
          >
            <input
              type="checkbox"
              id="smbgBlinded"
              bind:checked={$activityData.smbgBlinded}
              disabled={formFieldsDisabled}
              class="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            />
            <label for="smbgBlinded" class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >SMBG Blinded</label
            >
          </div>
        </div>

        <!-- Description -->
        <div class="flex gap-3">
          <div class="w-full">
            <label for="description" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Description</label
            >
            <textarea
              id="description"
              bind:value={$activityData.description}
              disabled={formFieldsDisabled}
              class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
              rows="3"
            ></textarea>
          </div>
        </div>

        <!-- Buttons -->
        <div class="flex justify-end space-x-2 pt-10">
          <button
            type="button"
            on:click={handleClose}
            class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-700"
            disabled={saveButtonDisabled && !isAdmin}
            class:opacity-50={saveButtonDisabled && !isAdmin}
            class:cursor-not-allowed={saveButtonDisabled && !isAdmin}
            title={saveButtonDisabled && !isAdmin ? 'Insufficient permissions' :
                    scheduleStatus !== 'Draft' && !isAdmin ? 'Activities can only be modified when schedule is in Draft status' :
                    'Save changes'}
          >
            {editActivity ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
